import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, SALES_EMAIL, AGENT_NAME } from '../config';
import {
  Check, ArrowRight, Star,
  TrendingUp, TrendingDown, Trophy, BarChart2,
  Activity, Zap, Users, Radio, Award, ShieldCheck,
} from 'lucide-react';

// Icon lookup for toasts and dynamic pill construction
const ICON_MAP = {
  up:       TrendingUp,
  down:     TrendingDown,
  trophy:   Trophy,
  chart:    BarChart2,
  activity: Activity,
  zap:      Zap,
  users:    Users,
  radio:    Radio,
  award:    Award,
  shield:   ShieldCheck,
};
import TraderBroLogo from '../assets/traderbro-logo.svg';

// ─── Custom Hooks ─────────────────────────────────────────────────────────────

function useInView(ref, threshold = 0.12) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return inView;
}

function useCountUp(target, duration = 1400, trigger = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let raf;
    const startTime = performance.now();
    function update(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(update);
      else setCount(target);
    }
    raf = requestAnimationFrame(update);
    return () => cancelAnimationFrame(raf);
  }, [trigger, target, duration]);
  return count;
}

// Kimpton-style: requestAnimationFrame scroll progress
function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let raf;
    function read() {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? window.scrollY / total : 0);
      raf = requestAnimationFrame(read);
    }
    raf = requestAnimationFrame(read);
    return () => cancelAnimationFrame(raf);
  }, []);
  return progress;
}

// Fetches predictions + symbols ONCE, shared by Ticker / Feed / Toasts / Bento
function useHomepageData() {
  const [predictions,          setPredictions]          = useState([]);
  const [symbols,              setSymbols]              = useState([]);
  const [semiconductorAnalysts, setSemiconductorAnalysts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/research/predictions/?ordering=-published_at&page=1&page_size=8`)
      .then(res => {
        const r = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        if (r.length) setPredictions(r.slice(0, 8));
      })
      .catch(() => {});

    axios
      .get(`${API_BASE_URL}/api/research/symbols/?ordering=-analyst_count&page=1&page_size=6`)
      .then(res => {
        const r = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        if (r.length) setSymbols(r.slice(0, 6));
      })
      .catch(() => {});

    axios
      .get(`${API_BASE_URL}/api/research/semiconductor-symbols/`)
      .then(res => {
        const r = Array.isArray(res.data) ? res.data : [];
        if (r.length) setSemiconductorAnalysts(r);
      })
      .catch(() => {});
  }, []);

  return { predictions, symbols, semiconductorAnalysts };
}

// ─── Shared Design Components ─────────────────────────────────────────────────

function SectionLabel({ children, color = 'emerald', dot = false }) {
  const colorCls = color === 'amber' ? 'text-amber-600' : 'text-emerald-600';
  const lineCls  = color === 'amber' ? 'bg-amber-500/40' : 'bg-emerald-500/40';
  return (
    <div className="flex items-center justify-center gap-3 mb-4">
      <div className={`w-8 h-px ${lineCls}`} />
      <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-widest ${colorCls}`}>
        {dot && <span className="hp-live-dot" />}
        <span>{children}</span>
      </div>
      <div className={`w-8 h-px ${lineCls}`} />
    </div>
  );
}

// ─── Utility & Transformer Functions ─────────────────────────────────────────

function timeAgo(isoString) {
  if (!isoString) return '';
  const sec = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (sec < 60)  return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60)  return `${min}m ago`;
  const hr  = Math.floor(min / 60);
  if (hr  < 24)  return `${hr}h ago`;
  const day = Math.floor(hr  / 24);
  if (day < 30)  return `${day}d ago`;
  return `${Math.floor(day / 30)}mo ago`;
}

function formatResult(direction, current_return_pct) {
  if (current_return_pct === null || current_return_pct === undefined) return 'Live';
  const pct = parseFloat(current_return_pct);
  if (isNaN(pct)) return 'Live';
  if (direction === 'bullish') return pct >= 0 ? `Hit! +${pct.toFixed(1)}%` : `Miss ${pct.toFixed(1)}%`;
  if (direction === 'bearish') return pct <= 0 ? `Hit! ${pct.toFixed(1)}%` : `Miss +${pct.toFixed(1)}%`;
  return pct >= 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`;
}

function capitalize(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

function predictionToTickerItem(p) {
  return {
    bull:   p.direction === 'bullish',
    handle: '@' + p.analyst_name.replace(/\s+/g, ''),
    ticker: '$' + p.symbol_ticker,
    call:   capitalize(p.direction) + (p.stated_price_target ? ' $' + parseFloat(p.stated_price_target).toFixed(0) : ''),
    result: formatResult(p.direction, p.current_return_pct),
  };
}

function predictionToFeedCard(p) {
  const retVal = p.current_return_pct !== null && p.current_return_pct !== undefined
    ? parseFloat(p.current_return_pct)
    : null;
  return {
    handle: '@' + p.analyst_name.replace(/\s+/g, ''),
    avatar: p.analyst_image_url ?? null,
    ticker: '$' + p.symbol_ticker,
    bull:   p.direction === 'bullish',
    date:   timeAgo(p.published_at),
    ret:    retVal !== null && !isNaN(retVal)
      ? (retVal >= 0 ? '+' : '') + retVal.toFixed(1) + '%'
      : 'Live',
  };
}

function predictionToToast(p) {
  const handle = '@' + p.analyst_name.replace(/\s+/g, '');
  const ticker = '$' + p.symbol_ticker;
  const retVal = p.current_return_pct !== null ? parseFloat(p.current_return_pct) : null;
  if (retVal !== null && !isNaN(retVal)) {
    const positive = retVal >= 0;
    return {
      iconKey:  positive ? 'up' : 'down',
      iconColor: positive ? '#10B981' : '#EF4444',
      iconBg:   positive ? '#f0fdf4' : '#fef2f2',
      sub:  'Prediction scored',
      text: `${handle} called ${ticker} — ${retVal >= 0 ? '+' : ''}${retVal.toFixed(1)}% return`,
    };
  }
  return {
    iconKey:  'chart',
    iconColor: '#4D7CFF',
    iconBg:   '#eff4ff',
    sub:  'New prediction',
    text: `${handle} just called ${ticker} ${capitalize(p.direction)}`,
  };
}

// ─── Section 0: Prediction Ticker ────────────────────────────────────────────

// Fallback hardcoded data shown when API returns nothing (e.g. empty local backend)
const TICKER_ITEMS_FALLBACK = [
  { bull: true,  handle: '@ArielHernandez', ticker: '$NVDA', call: 'Bullish $210',  result: 'Hit! +14.2%' },
  { bull: false, handle: '@JerryCapital',   ticker: '$META', call: 'Bearish $450',  result: 'Miss -8.1%'  },
  { bull: true,  handle: '@Gaetano',        ticker: '$TSLA', call: 'Bullish $280',  result: 'Live +3.4%'  },
  { bull: true,  handle: '@Serenity',       ticker: '$AAPL', call: 'Bullish $195',  result: 'Hit! +6.7%'  },
  { bull: false, handle: '@NickDrendel',    ticker: '$BBAI', call: 'Bearish $4.50', result: 'Hit! +22.1%' },
  { bull: true,  handle: '@TheValueTrader', ticker: '$AMZN', call: 'Bullish $180',  result: 'Hit! +9.3%'  },
  { bull: false, handle: '@CKCapital',      ticker: '$MSFT', call: 'Bearish $400',  result: 'Miss +4.5%'  },
  { bull: true,  handle: '@SixSigmaCapital',ticker: '$GOOGL',call: 'Bullish $160',  result: 'Live +1.8%'  },
];

function PredictionTicker({ predictions }) {
  const items = predictions.length > 0
    ? predictions.map(predictionToTickerItem)
    : TICKER_ITEMS_FALLBACK;

  return (
    // Dark ticker strip at top — intentional branded contrast (Bloomberg-style terminal)
    <div className="w-full bg-gray-900 border-b border-gray-800 py-2.5 overflow-hidden">
      <div className="hp-ticker-track">
        {[...items, ...items].map((item, i) => {
          const dot    = item.bull ? '🟢' : '🔴';
          const resCls = item.result.startsWith('Hit')
            ? 'text-emerald-400'
            : item.result.startsWith('Live') ? 'text-amber-400' : 'text-red-400';
          return (
            <span key={i} className="flex items-center gap-2 px-8 text-sm font-mono whitespace-nowrap border-r border-gray-700">
              <span>{dot}</span>
              <span className="text-emerald-400 font-semibold">{item.handle}:</span>
              <span className="text-white font-bold">{item.ticker}</span>
              <span className="text-gray-400">{item.call}</span>
              <span className="text-gray-600">→</span>
              <span className={`font-bold ${resCls}`}>{item.result}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Scroll Progress Bar ──────────────────────────────────────────────────────

function ScrollProgressBar() {
  const progress = useScrollProgress();
  return (
    <div
      className="hp-scroll-progress"
      style={{ width: '100%', transform: `scaleX(${progress})` }}
    />
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav className={`transition-all duration-300 ${
      scrolled
        ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm'
        : 'bg-white border-b border-gray-100'
    }`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <img src={TraderBroLogo} alt="TraderBro" className="h-7 w-auto select-none" />
          <div className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/leaderboard" className="text-gray-500 hover:text-gray-900 transition-colors">Leaderboard</Link>
            <Link to="/predictions" className="text-gray-500 hover:text-gray-900 transition-colors">Predictions</Link>
            <Link to="/symbols"     className="text-gray-500 hover:text-gray-900 transition-colors">Symbols</Link>
            <a href="#pricing"      className="text-gray-500 hover:text-gray-900 transition-colors">Pricing</a>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-gray-500 hover:text-gray-900 text-sm px-4 py-2 transition-colors">Log in</Link>
          <Link
            to="/login"
            className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-colors hp-btn-glow hp-btn-shimmer"
          >
            Start Free →
          </Link>
        </div>
      </div>
    </nav>
  );
}

// ─── Section 1: Hero ──────────────────────────────────────────────────────────

const TRUST_BADGES = [
  { icon: '✓', label: 'Real Data'     },
  { icon: '✓', label: 'Auto-Verified' },
  { icon: '✓', label: 'Zero Opinions' },
  { icon: '✓', label: '100+ Analysts' },
  { icon: '✓', label: '4 Platforms'   },
];

function StatCounter({ target, suffix = '', label, trigger }) {
  const count = useCountUp(target, 1500, trigger);
  return (
    <div className="py-7 px-5 text-center">
      <div className="text-3xl md:text-4xl font-black text-gray-900 tabular-nums leading-none">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-gray-400 text-xs mt-2 font-medium leading-snug">{label}</div>
    </div>
  );
}

// ─── Hero Stats Scroller ──────────────────────────────────────────────────────

// Fallback pills shown while APIs load or when backend is empty
const SCROLLER_FB_ROW1 = [
  { dot: '#0052FF', Icon: Users,       iconColor: '#0052FF', text: '100+ finfluencers tracked'             },
  { dot: '#10B981', Icon: TrendingUp,  iconColor: '#10B981', text: '@ArielHernandez: $NVDA Hit! +14.2%'   },
  { dot: '#4D7CFF', Icon: Trophy,      iconColor: '#4D7CFF', text: '#1 @ArielHernandez · 59% accuracy'    },
  { dot: '#10B981', Icon: TrendingUp,  iconColor: '#10B981', text: '@Serenity: $AAPL Hit! +6.7%'          },
  { dot: '#4D7CFF', Icon: Award,       iconColor: '#4D7CFF', text: '#2 @Serenity · 58% accuracy'          },
  { dot: '#f59e0b', Icon: Activity,    iconColor: '#f59e0b', text: '@Gaetano called $TSLA Bullish'        },
  { dot: '#0052FF', Icon: Radio,       iconColor: '#0052FF', text: '4 platforms monitored'                 },
  { dot: '#EF4444', Icon: TrendingDown,iconColor: '#EF4444', text: '@JerryCapital: $META Miss -8.1%'      },
  { dot: '#6d8eff', Icon: ShieldCheck, iconColor: '#6d8eff', text: '#3 @TheValueTrader · 49% accuracy'    },
  { dot: '#10B981', Icon: TrendingUp,  iconColor: '#10B981', text: '@TheValueTrader: $AMZN Hit! +9.3%'    },
];

const SCROLLER_FB_ROW2 = [
  { dot: '#10B981', Icon: Trophy,      iconColor: '#10B981', text: '@Gaetano leads: +20.0% avg return'    },
  { dot: '#60a5fa', Icon: BarChart2,   iconColor: '#60a5fa', text: '3,000+ predictions scored'            },
  { dot: '#f59e0b', Icon: TrendingUp,  iconColor: '#f59e0b', text: '@ArielHernandez: +5.9% avg return'   },
  { dot: '#93b4ff', Icon: Users,       iconColor: '#93b4ff', text: '$NVDA · 21 analysts · 61 predictions' },
  { dot: '#10B981', Icon: TrendingUp,  iconColor: '#10B981', text: '@SixSigmaCapital: $GOOGL +1.8%'       },
  { dot: '#60a5fa', Icon: Users,       iconColor: '#60a5fa', text: '$TSLA · 18 analysts tracking'         },
  { dot: '#0052FF', Icon: Zap,         iconColor: '#0052FF', text: 'Zero opinions — just math'            },
  { dot: '#4D7CFF', Icon: Users,       iconColor: '#4D7CFF', text: '$AAPL · 15 analysts · 38 predictions' },
  { dot: '#10B981', Icon: TrendingUp,  iconColor: '#10B981', text: '@CKCapital: $MSFT +4.5%'              },
  { dot: '#93b4ff', Icon: Users,       iconColor: '#93b4ff', text: '$META · 12 analysts tracking'         },
];

function buildScrollerPills(accuracyAnalysts, returnAnalysts, predictions, symbols) {
  const row1 = [...SCROLLER_FB_ROW1];
  const row2 = [...SCROLLER_FB_ROW2];

  // Inject live accuracy ranks (top 5)
  if (accuracyAnalysts.length) {
    const dotColors = ['#0052FF', '#4D7CFF', '#6d8eff', '#93b4ff', '#bfd3ff'];
    const rankIcons = [Trophy, Trophy, Award, Award, ShieldCheck];
    const rankPills = accuracyAnalysts.slice(0, 5).map((a, i) => ({
      dot: dotColors[i],
      Icon: rankIcons[i],
      iconColor: dotColors[i],
      text: `#${i + 1} @${a.name?.replace(/\s+/g, '') ?? 'Analyst'} · ${parseFloat(a.accuracy_rate ?? 0).toFixed(0)}% accuracy`,
    }));
    row1.splice(2, 3, ...rankPills.slice(0, 3));
  }

  // Inject top return leader into row2
  if (returnAnalysts.length) {
    const leader = returnAnalysts[0];
    const ret = parseFloat(leader.overall_return_pct ?? 0);
    row2[0] = {
      dot: '#10B981',
      Icon: Trophy,
      iconColor: '#10B981',
      text: `@${leader.name?.replace(/\s+/g, '') ?? 'Analyst'} leads: ${ret >= 0 ? '+' : ''}${ret.toFixed(1)}% avg return`,
    };
    if (returnAnalysts[1]) {
      const r2 = returnAnalysts[1];
      const r2ret = parseFloat(r2.overall_return_pct ?? 0);
      row2[2] = {
        dot: '#f59e0b',
        Icon: TrendingUp,
        iconColor: '#f59e0b',
        text: `@${r2.name?.replace(/\s+/g, '') ?? 'Analyst'}: ${r2ret >= 0 ? '+' : ''}${r2ret.toFixed(1)}% avg return`,
      };
    }
  }

  // Inject recent scored predictions into row1
  const scoredPreds = predictions
    .filter(p => p.current_return_pct !== null && p.current_return_pct !== undefined)
    .slice(0, 4);
  if (scoredPreds.length) {
    const predPills = scoredPreds.map(p => {
      const ret = parseFloat(p.current_return_pct);
      const hit = p.direction === 'bullish' ? ret >= 0 : ret <= 0;
      return {
        dot: hit ? '#10B981' : '#EF4444',
        Icon: hit ? TrendingUp : TrendingDown,
        iconColor: hit ? '#10B981' : '#EF4444',
        text: `@${p.analyst_name?.replace(/\s+/g, '') ?? '?'}: $${p.symbol_ticker} ${hit ? 'Hit!' : 'Miss'} ${ret >= 0 ? '+' : ''}${ret.toFixed(1)}%`,
      };
    });
    row1.splice(1, 2, ...predPills.slice(0, 2));
    row2.splice(4, 2, ...predPills.slice(2, 4));
  }

  // Inject top 2 symbols by analyst_count into row2
  const sortedSymbols = [...symbols].sort((a, b) => (b.analyst_count ?? 0) - (a.analyst_count ?? 0));
  const topSymbols = sortedSymbols.slice(0, 2);
  if (topSymbols.length) {
    topSymbols.forEach((s, i) => {
      row2[3 + i] = {
        dot: '#93b4ff',
        Icon: Users,
        iconColor: '#93b4ff',
        text: `$${s.ticker} · ${s.analyst_count ?? '?'} analysts · ${s.prediction_count ?? '?'} predictions`,
      };
    });
  }

  return { row1, row2 };
}

function HeroStatsScroller({ predictions, symbols }) {
  const [accuracyAnalysts, setAccuracyAnalysts] = useState([]);
  const [returnAnalysts,   setReturnAnalysts]   = useState([]);

  useEffect(() => {
    // Top 5 by leaderboard accuracy rank
    axios
      .get(`${API_BASE_URL}/api/research/analysts/?ordering=leaderboard_score&limit=5`)
      .then(res => { if (res.data?.length) setAccuracyAnalysts(res.data); })
      .catch(() => {});

    // Top 3 by highest average return
    axios
      .get(`${API_BASE_URL}/api/research/analysts/?ordering=-overall_return_pct&limit=3`)
      .then(res => { if (res.data?.length) setReturnAnalysts(res.data); })
      .catch(() => {});
  }, []);

  const { row1, row2 } = buildScrollerPills(accuracyAnalysts, returnAnalysts, predictions, symbols);

  // Duplicate for seamless loop
  const r1 = [...row1, ...row1];
  const r2 = [...row2, ...row2];

  return (
    <div className="relative w-full max-w-2xl mx-auto overflow-hidden">
      {/* Edge fade — left */}
      <div className="pointer-events-none absolute left-0 top-0 h-full w-12 bg-gradient-to-r from-white to-transparent z-10" />
      {/* Edge fade — right */}
      <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent z-10" />

      {/* Row 1 — scrolls left */}
      <div className="overflow-hidden mb-3">
        <div className="hp-ticker-track gap-2.5">
          {r1.map((p, i) => {
            const PillIcon = p.Icon;
            return (
              <span
                key={i}
                className="inline-flex items-center rounded-full border border-black/[0.07] bg-white/90 backdrop-blur px-5 py-2.5 shadow-sm flex-shrink-0 gap-2"
              >
                {PillIcon
                  ? <PillIcon size={15} style={{ color: p.iconColor, flexShrink: 0 }} strokeWidth={2.2} />
                  : <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.dot }} />
                }
                <span className="text-sm text-gray-800 font-medium whitespace-nowrap">{p.text}</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Row 2 — scrolls right */}
      <div className="overflow-hidden">
        <div className="hp-ticker-track-rev gap-2.5">
          {r2.map((p, i) => {
            const PillIcon = p.Icon;
            return (
              <span
                key={i}
                className="inline-flex items-center rounded-full border border-black/[0.07] bg-white/90 backdrop-blur px-5 py-2.5 shadow-sm flex-shrink-0 gap-2"
              >
                {PillIcon
                  ? <PillIcon size={15} style={{ color: p.iconColor, flexShrink: 0 }} strokeWidth={2.2} />
                  : <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.dot }} />
                }
                <span className="text-sm text-gray-800 font-medium whitespace-nowrap">{p.text}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HeroSection({ predictions, symbols }) {
  const ref    = useRef(null);
  const inView = useInView(ref, 0.05);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white">
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-emerald-100 rounded-full blur-[120px] opacity-60" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-teal-100 rounded-full blur-[100px] opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-100 rounded-full blur-[80px] opacity-40" />
      </div>

      <div className="relative z-10 max-w-4xl w-full">
        <div className={`hp-reveal ${inView ? 'hp-in' : ''} inline-block mb-7`}>
          <span className="border border-emerald-200 text-emerald-700 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest bg-emerald-50">
            🎯 The Credit Score for Financial Influencers
          </span>
        </div>

        <h1 className={`hp-reveal hp-d1 ${inView ? 'hp-in' : ''} text-6xl md:text-7xl lg:text-[84px] font-black leading-[0.93] mb-6 tracking-tight`}>
          <span className="text-gray-900">Who's Actually</span>
          <br />
          <span className="hp-grad-text">Right?</span>
        </h1>

        <p className={`hp-reveal hp-d2 ${inView ? 'hp-in' : ''} text-gray-500 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed`}>
          We track 100+ finfluencers across Twitter, YouTube, TikTok &amp; Instagram. Every prediction
          extracted. Every call scored against real market data. Zero opinions — just math.
        </p>

        <div className={`hp-reveal hp-d3 ${inView ? 'hp-in' : ''} flex flex-col sm:flex-row items-center justify-center gap-4 mb-10`}>
          <Link
            to="/leaderboard"
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-4 rounded-xl transition-all hp-btn-glow hp-btn-shimmer flex items-center gap-2 text-base w-full sm:w-auto justify-center"
          >
            Explore Leaderboard <ArrowRight size={18} />
          </Link>
          <a
            href="#how-it-works"
            className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-semibold px-8 py-4 rounded-xl transition-all text-base w-full sm:w-auto text-center"
          >
            See How It Works
          </a>
        </div>

        {/* Trust credential row */}
        <div className={`hp-reveal hp-d3 ${inView ? 'hp-in' : ''} flex flex-wrap items-center justify-center gap-3 mb-6`}>
          {TRUST_BADGES.map((b, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
              <span className="text-emerald-500 font-bold">{b.icon}</span>
              {b.label}
              {i < TRUST_BADGES.length - 1 && <span className="ml-3 text-gray-300">·</span>}
            </span>
          ))}
        </div>

        {/* CLI + Telegram strip */}
        <div className={`hp-reveal hp-d3 ${inView ? 'hp-in' : ''} flex items-center justify-center gap-3 mb-12 flex-wrap`}>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm text-sm text-gray-500">
            <span className="text-gray-400 font-medium">Also available as CLI · works with</span>
            {/* Agent avatars */}
            <div className="flex items-center -space-x-1.5">
              <span className="w-5 h-5 rounded-full bg-[#CC785C] flex items-center justify-center ring-2 ring-white flex-shrink-0">
                <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-white"><path d="M13.827 3.41c-.892-1.817-3.447-1.817-4.34 0L2.65 16.56C1.818 18.26 3.054 20.25 4.97 20.25h14.747c1.916 0 3.152-1.99 2.322-3.69L13.827 3.41zm-2.17 1.006a.75.75 0 011.342 0l7.188 14.627a.75.75 0 01-.671 1.082H4.97a.75.75 0 01-.67-1.082l7.357-14.627z"/></svg>
              </span>
              <span className="w-5 h-5 rounded-full bg-[#10A37F] flex items-center justify-center ring-2 ring-white flex-shrink-0">
                <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-white"><path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.032.067L9.73 19.95a4.5 4.5 0 01-6.13-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/></svg>
              </span>
              <span className="w-5 h-5 rounded-full bg-[#7C3AED] flex items-center justify-center ring-2 ring-white flex-shrink-0 text-white text-[8px] font-black">H</span>
              <span className="w-5 h-5 rounded-full bg-[#2563EB] flex items-center justify-center ring-2 ring-white flex-shrink-0 text-white text-[8px] font-black">O</span>
            </div>
            <span className="text-gray-300">·</span>
          </div>
          <a
            href="https://t.me/traderbro_cli_bot"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#229ED9] hover:bg-[#1a8bbf] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors shadow-sm"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
            </svg>
            Try CLI Demo
          </a>
        </div>

        <div className={`hp-reveal hp-d4 ${inView ? 'hp-in' : ''} w-full`}>
          <HeroStatsScroller predictions={predictions} symbols={symbols} />
        </div>
      </div>
    </section>
  );
}

// ─── Section 2: Leaderboard Preview ──────────────────────────────────────────

const SAMPLE_ANALYSTS = [
  { name: 'Ariel Hernandez',      accuracy_rate: '59.0', predictions_count: 280, overall_return_pct: '5.9',  slug: 'ariel-hernandez', sectors: ['Semiconductors','AI']   },
  { name: 'Gaetano',              accuracy_rate: '51.0', predictions_count: 178, overall_return_pct: '20.0', slug: 'gaetano',         sectors: ['EV','Clean Energy']      },
  { name: 'The Value Trader',     accuracy_rate: '49.0', predictions_count: 178, overall_return_pct: '4.9',  slug: 'the-value-trader', sectors: ['Large Cap','Value']     },
  { name: 'Serenity',             accuracy_rate: '58.0', predictions_count: 162, overall_return_pct: '4.3',  slug: 'serenity',        sectors: ['AI','Tech']              },
  { name: 'Oliver | MMMT Wealth', accuracy_rate: '34.0', predictions_count: 149, overall_return_pct: '-6.1', slug: 'oliver-mmmt',     sectors: ['Crypto','Macro']         },
];

function AnalystCard({ analyst, rank, delay, parentInView }) {
  const avgReturn = parseFloat(analyst.overall_return_pct ?? 0);
  const retCls    = avgReturn >= 0 ? 'text-emerald-600' : 'text-red-500';

  return (
    <div
      className={`bg-white border border-gray-200 shadow-sm rounded-2xl p-4 flex flex-col gap-3 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-md transition-all duration-300 hp-reveal ${parentInView ? 'hp-in' : ''}`}
      style={{ transitionDelay: `${delay}ms`, transitionDuration: '620ms' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 font-black text-sm flex-shrink-0 overflow-hidden">
            {analyst.profile_image_url
              ? <img src={analyst.profile_image_url} alt={analyst.name} className="w-full h-full object-cover" />
              : analyst.name?.[0]}
          </div>
          <div>
            <div className="text-gray-900 font-semibold text-sm leading-tight line-clamp-1">{analyst.name}</div>
            <div className="text-gray-400 text-xs">{analyst.predictions_count} predictions</div>
          </div>
        </div>
        <span className="text-xs font-black text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded font-mono flex-shrink-0">
          #{rank}
        </span>
      </div>

      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-400">Avg Return</span>
        <span className={`text-sm font-bold font-mono ${retCls}`}>
          {avgReturn >= 0 ? '+' : ''}{avgReturn.toFixed(1)}%
        </span>
      </div>

      {analyst.sectors?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {analyst.sectors.map(s => (
            <span key={s} className="text-[9px] font-bold uppercase tracking-wider text-gray-400 border border-gray-200 bg-gray-50 px-2 py-0.5 rounded-full">
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function LeaderboardPreview() {
  const [analysts, setAnalysts] = useState(SAMPLE_ANALYSTS);
  const ref    = useRef(null);
  const inView = useInView(ref, 0.1);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/research/analysts/?ordering=leaderboard_score&limit=5`)
      .then(res => {
        if (res.data?.length) {
          const tagged = res.data.slice(0, 5).map((a, i) => ({
            ...a,
            sectors: SAMPLE_ANALYSTS[i]?.sectors ?? [],
          }));
          setAnalysts(tagged);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section className="py-24 bg-gray-50 px-6">
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className={`text-center mb-10 hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel dot>Live Rankings</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Who's Actually Right?</h2>
          <p className="text-gray-500 text-lg">Real-time accuracy rankings — not followers, not engagement. Math.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-12 items-start">
          {analysts.map((a, i) => (
            <AnalystCard key={i} analyst={a} rank={i + 1} delay={i * 130} parentInView={inView} />
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/leaderboard"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold border border-emerald-200 px-6 py-3 rounded-xl transition-all hover:bg-emerald-50"
          >
            See Full Leaderboard <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Section 3: Prediction Feed Simulation ────────────────────────────────────

const FEED_CARDS_FALLBACK = [
  { handle: '@ArielHernandez', ticker: '$NVDA', bull: true,  date: '2h ago',  ret: '+14.2%' },
  { handle: '@Gaetano',        ticker: '$TSLA', bull: true,  date: '4h ago',  ret: '+3.4%'  },
  { handle: '@JerryCapital',   ticker: '$META', bull: false, date: '5h ago',  ret: '-8.1%'  },
  { handle: '@Serenity',       ticker: '$AAPL', bull: true,  date: '7h ago',  ret: '+6.7%'  },
  { handle: '@NickDrendel',    ticker: '$BBAI', bull: false, date: '9h ago',  ret: '+22.1%' },
  { handle: '@TheValueTrader', ticker: '$AMZN', bull: true,  date: '11h ago', ret: '+9.3%'  },
  { handle: '@CKCapital',      ticker: '$MSFT', bull: false, date: '14h ago', ret: '+4.5%'  },
  { handle: '@SixSigmaCapital',ticker: '$GOOGL',bull: true,  date: '16h ago', ret: '+1.8%'  },
];

function PredictionFeedSim({ predictions }) {
  const cards    = predictions.length > 0 ? predictions.map(predictionToFeedCard) : FEED_CARDS_FALLBACK;
  const cardsRef = useRef(cards);
  useEffect(() => { cardsRef.current = cards; }, [cards]);

  const [stack,  setStack]  = useState(() => cards.slice(0, 4));
  const [cursor, setCursor] = useState(4);
  const ref    = useRef(null);
  const inView = useInView(ref, 0.15);

  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => {
      setCursor(prev => {
        const src  = cardsRef.current;
        const next = prev % src.length;
        setStack(s => [src[next], ...s.slice(0, 3)]);
        return next + 1;
      });
    }, 2500);
    return () => clearInterval(id);
  }, [inView]);

  return (
    <section className="py-24 bg-white px-6">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
        <div ref={ref} className={`hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel dot>Live Feed</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
            Every Call.<br />Tracked in Real Time.
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed mb-10">
            Every prediction from every finfluencer — extracted, logged, and scored automatically.
            No cherry-picking. No hindsight. Pure accuracy data.
          </p>
          <Link
            to="/predictions"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold border border-emerald-200 px-6 py-3 rounded-xl transition-all hover:bg-emerald-50"
          >
            Browse All Predictions <ArrowRight size={16} />
          </Link>
        </div>

        <div className="relative h-[320px] overflow-hidden rounded-2xl">
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" />
          <div className="space-y-3">
            {stack.map((p, i) => (
              <div
                key={`${p.handle}-${i}`}
                className={`bg-white border border-gray-200 shadow-sm rounded-xl px-4 py-3 flex items-center gap-3 ${i === 0 ? 'hp-slide-right' : ''}`}
                style={{ opacity: 1 - i * 0.18 }}
              >
                <div className={`w-8 h-8 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-xs font-black ${p.bull ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                  {p.avatar
                    ? <img src={p.avatar} alt={p.handle} className="w-full h-full object-cover" />
                    : (p.handle[1]?.toUpperCase() ?? '?')
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-gray-900 text-sm font-semibold truncate">{p.handle}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span className="font-mono font-bold text-gray-900">{p.ticker}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.bull ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                      {p.bull ? 'BULLISH' : 'BEARISH'}
                    </span>
                    <span>{p.date}</span>
                  </div>
                </div>
                <span className={`text-sm font-bold font-mono flex-shrink-0 ${p.ret === 'Live' ? 'text-amber-500' : p.ret.startsWith('+') ? 'text-emerald-600' : 'text-red-500'}`}>
                  {p.ret}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 4: How It Works Pipeline ────────────────────────────────────────

// ─── Section 4: How It Works (Dark) ──────────────────────────────────────────

const HIW_TABS = [
  {
    num: '01',
    label: 'Extract',
    title: 'Scan every finfluencer, everywhere',
    desc: 'Our NLP pipeline monitors 100+ accounts across Twitter/X, YouTube, TikTok, and Instagram. Every stock prediction is automatically detected and logged in real time — no manual curation.',
  },
  {
    num: '02',
    label: 'Classify',
    title: 'AI separates predictions from noise',
    desc: "Machine learning distinguishes real stock calls from opinions, sarcasm, and vague commentary. The classification engine is TraderBro's core technical moat.",
  },
  {
    num: '03',
    label: 'Score',
    title: 'Match predictions to market reality',
    desc: 'Each extracted prediction is auto-resolved against real price data at the stated timeframe expiry. Win, loss, or still live — every call gets a math-based verdict.',
  },
  {
    num: '04',
    label: 'Rank',
    title: 'Public leaderboard, ranked by accuracy',
    desc: "Every tracked finfluencer ranked by verified prediction accuracy. Not followers, not engagement — just who's actually right. Filterable by sector.",
  },
];

// ── Per-tab visual components ─────────────────────────────────────────────────

function ExtractViz() {
  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden"
      style={{ minHeight: 280, background: 'radial-gradient(87% 76% at 56% 50%, #0f2419 0%, #0a1a10 50%, #060d08 100%)' }}
    >
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 220" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="ef1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="ef2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34D399" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#34D399" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[60,100,140,180].map(y => (
          <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        ))}
        <path d="M 0,160 C 80,145 140,110 200,82 S 310,48 400,28 L 400,220 L 0,220 Z" fill="url(#ef1)" />
        <path d="M 0,178 C 80,163 140,130 200,106 S 310,74 400,56 L 400,220 L 0,220 Z" fill="url(#ef2)" />
        <path d="M 0,160 C 80,145 140,110 200,82 S 310,48 400,28" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M 0,178 C 80,163 140,130 200,106 S 310,74 400,56" fill="none" stroke="#34D399" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.55" />
        <circle cx="400" cy="28" r="4" fill="#10B981" />
        <circle cx="400" cy="28" r="10" fill="#10B981" fillOpacity="0.15" className="hiw-dot-pulse" />
        <circle cx="400" cy="56" r="3" fill="#34D399" fillOpacity="0.7" />
      </svg>
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
        {['◉  4 Platforms', '◉  100+ Analysts'].map((t, i) => (
          <div key={i} className="bg-black/50 backdrop-blur-sm border border-white/10 text-white/80 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg">
            <span className="text-emerald-400">{t.slice(0,1)}</span>{t.slice(1)}
          </div>
        ))}
      </div>
      <div className="absolute bottom-4 left-4 z-10 bg-[#0a1a10]/95 backdrop-blur-md border border-emerald-900/60 rounded-xl p-3.5 shadow-2xl w-52">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 hiw-dot-pulse block flex-shrink-0" />
          <span className="text-white text-xs font-bold">Live Ingestion</span>
        </div>
        <p className="text-gray-500 text-[10px] mb-2.5">Scanning @ArielHernandez...</p>
        <div className="bg-black/60 rounded-md px-2.5 py-1.5 font-mono text-[9px] text-emerald-400 border border-emerald-900/40">
          api.traderbro.ai/v1/extract
        </div>
        <div className="mt-2 flex justify-end">
          <span className="text-[9px] font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">● Connected</span>
        </div>
      </div>
    </div>
  );
}

function ClassifyViz() {
  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden flex items-center justify-center p-6"
      style={{ minHeight: 280, background: 'radial-gradient(87% 76% at 44% 50%, #111827 0%, #0d1520 50%, #080c18 100%)' }}
    >
      <div className="relative w-full max-w-xs">
        <div className="bg-[#0d1f14] border-2 border-emerald-500/50 rounded-2xl p-4 shadow-xl shadow-emerald-900/20 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-emerald-900/60 text-emerald-400 text-[10px] font-black flex items-center justify-center flex-shrink-0">A</div>
            <span className="text-white/80 text-xs font-semibold">@ArielHernandez</span>
            <span className="ml-auto text-[9px] font-black text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full whitespace-nowrap">✅ Prediction</span>
          </div>
          <p className="text-white/70 text-xs leading-relaxed mb-2.5">"NVDA to $210 by end of Q2, adding here at $162"</p>
          <div className="flex flex-wrap gap-1">
            {['NVDA', '$210', 'Q2'].map((t, i) => (
              <span key={i} className="text-[9px] font-mono text-emerald-400/80 bg-emerald-900/40 border border-emerald-900/60 px-1.5 py-0.5 rounded">{t}</span>
            ))}
          </div>
        </div>
        <div className="ml-6 bg-[#1a0d0d] border border-red-900/50 rounded-2xl p-4 shadow-xl opacity-75">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-red-900/40 text-red-400 text-[10px] font-black flex items-center justify-center flex-shrink-0">T</div>
            <span className="text-white/50 text-xs font-semibold">@TheValueTrader</span>
            <span className="ml-auto text-[9px] font-black text-red-400 bg-red-400/10 border border-red-400/20 px-2 py-0.5 rounded-full whitespace-nowrap">❌ Commentary</span>
          </div>
          <p className="text-white/40 text-xs leading-relaxed">"NVDA looks interesting here, might add some"</p>
        </div>
      </div>
    </div>
  );
}

function ScoreViz() {
  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden"
      style={{ minHeight: 280, background: 'radial-gradient(87% 76% at 50% 50%, #0e1628 0%, #0a1120 50%, #060a18 100%)' }}
    >
      <svg className="absolute inset-0 w-full h-full opacity-35" viewBox="0 0 400 220" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="sf1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M 0,180 L 60,162 L 120,174 L 180,132 L 240,92 L 300,68 L 400,34 L 400,220 L 0,220 Z" fill="url(#sf1)" />
        <path d="M 0,180 L 60,162 L 120,174 L 180,132 L 240,92 L 300,68 L 400,34" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="60" cy="162" r="5" fill="white" fillOpacity="0.5" />
        <circle cx="400" cy="34" r="5" fill="#10B981" />
        <circle cx="400" cy="34" r="11" fill="#10B981" fillOpacity="0.2" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <div className="bg-[#0d1828]/95 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl w-full max-w-xs">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
            <div>
              <div className="text-white font-black text-lg">$NVDA</div>
              <div className="text-gray-500 text-xs">Bullish ↑ · Q2 2024</div>
            </div>
            <div className="bg-emerald-500 text-white text-xs font-black px-3 py-1.5 rounded-lg shadow-lg shadow-emerald-900/40">WIN ✅</div>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { label:'Entry',  value:'$175.07', cls:'text-white/80'    },
              { label:'Target', value:'$210.00', cls:'text-blue-400'    },
              { label:'Return', value:'+13.3%',  cls:'text-emerald-400' },
            ].map((s, i) => (
              <div key={i} className="text-center bg-white/5 rounded-xl p-2.5 border border-white/5">
                <div className="text-[9px] text-gray-500 mb-1">{s.label}</div>
                <div className={`text-xs font-black ${s.cls}`}>{s.value}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] text-gray-600">Accuracy</div>
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full w-[74%] bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full" />
            </div>
            <span className="text-[10px] text-emerald-400 font-bold">✓</span>
          </div>
        </div>
      </div>
      <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm border border-white/10 text-white/80 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg z-10">
        3,000+ Scored
      </div>
    </div>
  );
}

function RankViz() {
  const rows = [
    { badge: '🥇', name: 'Ariel Hernandez',  acc: 59, calls: 280 },
    { badge: '🥈', name: 'Gaetano',           acc: 51, calls: 178 },
    { badge: '🥉', name: 'The Value Trader',  acc: 49, calls: 178 },
  ];
  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden flex items-center justify-center p-6"
      style={{ minHeight: 280, background: 'radial-gradient(87% 76% at 50% 50%, #0f1018 0%, #0a0b14 50%, #060710 100%)' }}
    >
      <div className="w-full max-w-xs bg-[#0d1020]/95 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
          <div className="text-white font-bold text-sm">Leaderboard</div>
          <span className="text-[10px] font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block hiw-dot-pulse" />
            Updated Live
          </span>
        </div>
        <div className="space-y-3.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-base flex-shrink-0">{r.badge}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white/80 text-xs font-semibold truncate">{r.name}</span>
                  <span className="text-emerald-400 text-xs font-black ml-2 flex-shrink-0">{r.acc}%</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full" style={{ width: `${r.acc}%` }} />
                </div>
                <div className="text-[10px] text-gray-600 mt-0.5">{r.calls} predictions</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm border border-white/10 text-white/80 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg">
        Public Rankings
      </div>
    </div>
  );
}

const HIW_VISUALS = [ExtractViz, ClassifyViz, ScoreViz, RankViz];

// ── Main HowItWorks component ─────────────────────────────────────────────────

function HowItWorksDark() {
  const ref    = useRef(null);
  const inView = useInView(ref, 0.15);

  const [activeTab, setActiveTab] = useState(0);
  const [shownTab,  setShownTab]  = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [autoPlay,  setAutoPlay]  = useState(true);
  const switchingRef = useRef(false);
  const pauseRef     = useRef(null);

  function goToTab(i, fromAuto = false) {
    if (i === activeTab || switchingRef.current) return;
    if (!fromAuto) {
      setAutoPlay(false);
      clearTimeout(pauseRef.current);
      pauseRef.current = setTimeout(() => setAutoPlay(true), 8000);
    }
    switchingRef.current = true;
    setActiveTab(i);
    setIsExiting(true);
    setTimeout(() => {
      setShownTab(i);
      setIsExiting(false);
      setTimeout(() => { switchingRef.current = false; }, 320);
    }, 150);
  }

  // Auto-advance every 5s while visible
  useEffect(() => {
    if (!autoPlay || !inView) return;
    const id = setTimeout(() => goToTab((activeTab + 1) % HIW_TABS.length, true), 5000);
    return () => clearTimeout(id);
  }, [activeTab, autoPlay, inView]);

  const Visual = HIW_VISUALS[shownTab];

  return (
    <section id="how-it-works" className="py-24 px-6" style={{ background: '#0A0F1C' }}>
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div ref={ref} className={`text-center mb-12 hp-reveal ${inView ? 'hp-in' : ''}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-px bg-emerald-500/40" />
            <span className="text-emerald-400 text-xs font-semibold uppercase tracking-widest">How It Works</span>
            <div className="w-8 h-px bg-emerald-500/40" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-3 leading-tight tracking-tight">
            From Tweet to Truth
          </h2>
          <p className="text-gray-500 text-lg">Fully automated, end-to-end verification engine.</p>
        </div>

        {/* Tab bar — desktop */}
        <div className={`hp-reveal hp-d1 ${inView ? 'hp-in' : ''} mb-8`}>
          <div className="hidden md:flex border-b border-white/10">
            {HIW_TABS.map((tab, i) => (
              <button
                key={i}
                onClick={() => goToTab(i)}
                className={`flex-1 text-left px-6 pt-3 pb-0 transition-all duration-300 ${
                  activeTab === i ? 'opacity-100' : 'opacity-35 hover:opacity-55'
                }`}
              >
                <div className="text-gray-600 text-[11px] font-bold tracking-widest mb-0.5">{tab.num}</div>
                <div className="text-white text-sm font-semibold mb-3">{tab.label}</div>
                <div className="h-[3px] w-full bg-white/10 rounded-full overflow-hidden">
                  {activeTab === i && (
                    <div
                      key={`p-${activeTab}-${autoPlay ? 1 : 0}`}
                      className="h-full bg-emerald-400 rounded-full"
                      style={{
                        animation: autoPlay && inView ? 'auto-tabs-progress 5000ms linear forwards' : 'none',
                        width: !autoPlay || !inView ? '100%' : undefined,
                      }}
                    />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Tab bar — mobile scroll */}
          <div className="md:hidden flex overflow-x-auto snap-x snap-mandatory scrollbar-hide border-b border-white/10">
            {HIW_TABS.map((tab, i) => (
              <button
                key={i}
                onClick={() => goToTab(i)}
                className={`snap-center flex-shrink-0 text-left px-5 pt-3 pb-0 transition-all duration-300 ${
                  activeTab === i ? 'opacity-100' : 'opacity-35'
                }`}
                style={{ minWidth: '55vw' }}
              >
                <div className="text-gray-600 text-[11px] font-bold tracking-widest mb-0.5">{tab.num}</div>
                <div className="text-white text-sm font-semibold mb-3">{tab.label}</div>
                <div className="h-[3px] w-full bg-white/10 rounded-full overflow-hidden">
                  {activeTab === i && (
                    <div
                      key={`mp-${activeTab}`}
                      className="h-full bg-emerald-400 rounded-full"
                      style={{ animation: autoPlay && inView ? 'auto-tabs-progress 5000ms linear forwards' : 'none' }}
                    />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content panel */}
        <div className={`hp-reveal hp-d2 ${inView ? 'hp-in' : ''}`}>
          <div
            key={shownTab}
            className={`rounded-3xl overflow-hidden ${isExiting ? 'hiw-content-exit' : 'hiw-content-enter'}`}
            style={{ background: '#0d1220', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="grid md:grid-cols-2 min-h-[440px] md:min-h-[400px]">
              {/* Left: text */}
              <div className="p-8 md:p-10 flex flex-col justify-center border-b md:border-b-0 md:border-r border-white/[0.05]">
                <h3 className="text-2xl font-black text-white mb-4 leading-tight">
                  {HIW_TABS[shownTab].title}
                </h3>
                <p className="text-gray-400 text-base leading-relaxed">
                  {HIW_TABS[shownTab].desc}
                </p>
              </div>
              {/* Right: rich visual */}
              <div className="p-4 md:p-6 flex items-stretch">
                <div className="w-full">
                  <Visual />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// ─── Section 5: Symbol Bento Grid ────────────────────────────────────────────

// Static sparklines for known tickers; unknown tickers get a gentle upward default
const STATIC_SPARKLINES = {
  NVDA: [60, 72, 65, 80, 78, 92, 88, 100],
  TSLA: [100, 85, 90, 78, 82, 70, 75, 80],
  AAPL: [70, 75, 73, 82, 85, 83, 90, 95],
  META: [55, 65, 60, 70, 68, 78, 75, 82],
  AMZN: [80, 82, 85, 88, 92, 89, 94, 98],
  MSFT: [75, 78, 80, 83, 82, 86, 88, 92],
};
const DEFAULT_SPARKLINE = [50, 52, 51, 55, 53, 57, 56, 60];

function symbolToBentoCard(s) {
  return {
    ticker:      s.ticker,
    name:        s.name,
    analysts:    s.analyst_count,
    predictions: s.prediction_count,
    bull:        s.bullish_count,
    bear:        s.bearish_count,
    price:       s.latest_close_price
      ? '$' + parseFloat(s.latest_close_price).toFixed(2)
      : 'N/A',
    sparkline: STATIC_SPARKLINES[s.ticker] ?? DEFAULT_SPARKLINE,
  };
}

const SYMBOLS_FALLBACK = [
  { ticker: 'NVDA', name: 'NVIDIA Corp',    analysts: 21, predictions: 61, bull: 46, bear: 14, price: '$175.07', sparkline: STATIC_SPARKLINES.NVDA },
  { ticker: 'TSLA', name: 'Tesla Inc',      analysts: 18, predictions: 45, bull: 30, bear: 15, price: '$242.50', sparkline: STATIC_SPARKLINES.TSLA },
  { ticker: 'AAPL', name: 'Apple Inc',      analysts: 15, predictions: 38, bull: 28, bear: 10, price: '$198.30', sparkline: STATIC_SPARKLINES.AAPL },
  { ticker: 'META', name: 'Meta Platforms', analysts: 12, predictions: 29, bull: 15, bear: 14, price: '$510.20', sparkline: STATIC_SPARKLINES.META },
  { ticker: 'AMZN', name: 'Amazon.com',     analysts: 10, predictions: 24, bull: 20, bear:  4, price: '$186.40', sparkline: STATIC_SPARKLINES.AMZN },
  { ticker: 'MSFT', name: 'Microsoft Corp', analysts: 14, predictions: 33, bull: 25, bear:  8, price: '$420.15', sparkline: STATIC_SPARKLINES.MSFT },
];

function Sparkline({ data, bullish }) {
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const W = 72, H = 28;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * W},${H - ((v - min) / range) * H}`).join(' ');
  return (
    <svg width={W} height={H} className="overflow-visible flex-shrink-0">
      <polyline points={pts} fill="none" stroke={bullish ? '#10B981' : '#EF4444'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SymbolBentoGrid({ symbols: symbolsProp }) {
  const displaySymbols = symbolsProp.length > 0
    ? symbolsProp.map(symbolToBentoCard)
    : SYMBOLS_FALLBACK;

  const ref    = useRef(null);
  const inView = useInView(ref, 0.1);

  return (
    <section className="py-24 bg-white px-6">
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className={`text-center mb-14 hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel>Coverage</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Every Stock. Every Finfluencer. Every Call.</h2>
          <p className="text-gray-500 text-lg">See which stocks get the most attention — and whether the calls are right.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
          {displaySymbols.map((s, i) => {
            const total   = (s.bull + s.bear) || 1;
            const bullPct = Math.round((s.bull / total) * 100);
            const bullish = bullPct >= 50;
            return (
              <div
                key={s.ticker}
                className={`bg-white border border-gray-200 shadow-sm rounded-2xl p-5 hover:scale-[1.02] hover:border-emerald-300 hover:shadow-md cursor-pointer transition-all duration-300 hp-reveal ${inView ? 'hp-in' : ''}`}
                style={{ transitionDelay: `${i * 75}ms`, transitionDuration: '600ms' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-gray-900 font-black text-lg font-mono">{s.ticker}</div>
                    <div className="text-gray-400 text-xs">{s.name}</div>
                  </div>
                  <Sparkline data={s.sparkline} bullish={bullish} />
                </div>
                <div className="text-gray-900 font-bold text-sm mb-3">{s.price}</div>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>{s.analysts} analysts · {s.predictions} calls</span>
                    <span className={bullish ? 'text-emerald-600' : 'text-red-500'}>{bullPct}% bull</span>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden flex">
                    <div
                      className="bg-emerald-500 h-full transition-all duration-1000 ease-out"
                      style={{ width: inView ? `${bullPct}%` : '0%', transitionDelay: `${i * 75 + 400}ms` }}
                    />
                    <div className="bg-red-200 h-full flex-1" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Link to="/symbols" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-colors">
            Explore All Symbols <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Section 6: Toast Notifications ──────────────────────────────────────────

const TOAST_MSGS_FALLBACK = [
  { iconKey: 'up',      iconColor: '#10B981', iconBg: '#f0fdf4', sub: 'Prediction scored',  text: '@ArielHernandez nailed $NVDA — +14.2% return'   },
  { iconKey: 'chart',   iconColor: '#4D7CFF', iconBg: '#eff4ff', sub: 'New prediction',      text: '@Gaetano just called $TSLA Bullish'              },
  { iconKey: 'trophy',  iconColor: '#f59e0b', iconBg: '#fffbeb', sub: 'Leaderboard update',  text: '@Serenity moved up to #4'                       },
  { iconKey: 'down',    iconColor: '#EF4444', iconBg: '#fef2f2', sub: 'Prediction resolved', text: "@JerryCapital's $META call missed by -8.1%"     },
  { iconKey: 'zap',     iconColor: '#6d8eff', iconBg: '#eff4ff', sub: 'Activity surge',       text: '3 new predictions logged in the last hour'     },
];

function ToastNotifications({ predictions }) {
  const msgs    = predictions.length > 0
    ? predictions.slice(0, 8).map(predictionToToast)
    : TOAST_MSGS_FALLBACK;
  const msgsRef = useRef(msgs);
  useEffect(() => { msgsRef.current = msgs; }, [msgs]);

  const [toasts, setToasts] = useState([]);
  const idx = useRef(0);

  useEffect(() => {
    function add() {
      const src = msgsRef.current;
      const msg = src[idx.current % src.length];
      const id  = Date.now();
      setToasts(prev => [...prev.slice(-2), { ...msg, id }]);
      idx.current++;
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4200);
    }
    const first = setTimeout(add, 2500);
    const timer = setInterval(add, 5000);
    return () => { clearTimeout(first); clearInterval(timer); };
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex-col gap-3 pointer-events-none w-72 hidden lg:flex">
      {toasts.map(t => {
        const ToastIcon = ICON_MAP[t.iconKey] ?? BarChart2;
        return (
          <div key={t.id} className="bg-white border border-gray-200 shadow-2xl rounded-2xl p-4 pointer-events-auto hp-slide-right">
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: t.iconBg ?? '#f3f4f6' }}
              >
                <ToastIcon size={15} style={{ color: t.iconColor ?? '#6b7280' }} strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: t.iconColor ?? '#6b7280' }}>{t.sub}</div>
                <div className="text-gray-900 text-sm leading-snug">{t.text}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Section 7: Perplexity Comparison ────────────────────────────────────────

const PERPLEXITY_TEXT =
  "I don't have access to real-time data on individual financial influencer prediction accuracy. While I can discuss general concepts, I cannot provide specific accuracy rates for finfluencers on semiconductor stocks. I'd recommend checking financial data platforms for this information.";

const TB_ANALYSTS = [
  { handle: 'ArielHernandez', tickers: 'NVDA/AMD',  accuracy: '72%', calls: 34, pos: true  },
  { handle: 'TheValueTrader', tickers: 'NVDA/INTC', accuracy: '61%', calls: 28, pos: true  },
  { handle: 'NickDrendel',    tickers: 'SMCI/AMD',  accuracy: '55%', calls: 19, pos: false },
];

function PerplexityComparison({ semiconductorAnalysts }) {
  const analysts = semiconductorAnalysts?.length > 2 ? semiconductorAnalysts : TB_ANALYSTS;
  const [typed,     setTyped]     = useState('');
  const [showRight, setShowRight] = useState(false);
  const ref    = useRef(null);
  const inView = useInView(ref, 0.2);

  useEffect(() => {
    if (!inView) return;
    let i = 0;
    const id = setInterval(() => {
      if (i < PERPLEXITY_TEXT.length) {
        setTyped(PERPLEXITY_TEXT.slice(0, ++i));
      } else {
        clearInterval(id);
        setTimeout(() => setShowRight(true), 350);
      }
    }, 16);
    return () => clearInterval(id);
  }, [inView]);

  return (
    <section className="py-24 bg-gray-50 px-6">
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className={`text-center mb-14 hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel>Unique Data</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">The Data Nobody Else Has</h2>
          <p className="text-gray-500 text-lg">Ask ChatGPT. Then ask TraderBro. See the difference.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: ChatGPT */}
          <div className="bg-white border border-gray-200 rounded-2xl p-7 shadow-sm">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                <span className="text-[9px] font-black text-white">AI</span>
              </div>
              <span className="text-gray-500 text-sm font-medium">ChatGPT / Perplexity</span>
            </div>
            <p className="text-gray-400 text-xs italic mb-4">Q: "Who is the most accurate finfluencer on semiconductor stocks?"</p>
            <p className="text-gray-600 text-sm leading-relaxed min-h-[120px]">
              {typed}
              {typed.length < PERPLEXITY_TEXT.length && inView && (
                <span className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 animate-pulse align-middle" />
              )}
            </p>
          </div>

          {/* Right: TraderBro */}
          <div
            className={`bg-emerald-50 border border-emerald-200 rounded-2xl p-7 shadow-sm transition-all duration-700 ${showRight ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-emerald-100">
              <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                <span className="text-[9px] font-black text-white">TB</span>
              </div>
              <span className="text-emerald-700 text-sm font-semibold">TraderBro</span>
              <div className="ml-auto flex items-center gap-1.5">
                <span className="hp-live-dot" style={{ width: 6, height: 6 }} />
                <span className="text-[9px] text-emerald-600 font-bold tracking-widest">LIVE</span>
              </div>
            </div>
            <p className="text-gray-400 text-xs italic mb-5">Top analysts for semiconductor stocks:</p>
            <div className="space-y-3">
              {analysts.map((a, i) => {
                const handle   = a.analyst_name  ?? a.handle;
                const tickers  = a.top_symbols   ? a.top_symbols.filter((v, idx, arr) => arr.indexOf(v) === idx).join('/') : a.tickers;
                const calls    = a.prediction_count ?? a.calls;
                const accPct   = a.accuracy_pct  ?? null;
                const accuracy = accPct !== null ? `${accPct}%` : a.accuracy;
                const pos      = accPct !== null ? accPct >= 50 : a.pos;
                return (
                  <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-emerald-100">
                    <span className="text-emerald-600 font-black font-mono text-xs">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 text-sm font-semibold">@{handle}</div>
                      <div className="text-gray-400 text-xs">{tickers} · {calls} calls</div>
                    </div>
                    <span className={`font-bold text-sm font-mono flex-shrink-0 ${pos ? 'text-emerald-600' : 'text-amber-500'}`}>
                      {accuracy}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials Strip ───────────────────────────────────────────────────────

const TESTIMONIALS = [
  {
    quote: "Finally stopped following hype. TraderBro shows me who actually has the receipts. My portfolio is up 31% since switching to data-driven analyst selection.",
    name: 'Michael R.',
    handle: '@investor_mike',
    role: 'Retail Investor',
    stars: 5,
  },
  {
    quote: "We use it for compliance screening. The accuracy data is the only objective, time-stamped record of influencer calls that exists. This is exactly what regulators will demand.",
    name: 'Sarah K.',
    handle: 'Portfolio Manager',
    role: 'Buy-side Fund',
    stars: 5,
  },
  {
    quote: "Filtered to analysts with 55%+ accuracy and 100+ predictions. Returns improved 23% year-over-year. The math doesn't lie.",
    name: 'James T.',
    handle: '@retail_alpha',
    role: 'Options Trader',
    stars: 5,
  },
];

function TestimonialsStrip() {
  const ref    = useRef(null);
  const inView = useInView(ref, 0.1);

  return (
    <section className="py-24 bg-white px-6">
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className={`text-center mb-14 hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel>Social Proof</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">What Traders Are Saying</h2>
          <p className="text-gray-500 text-lg">From retail investors to compliance teams — the data speaks.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-sm rounded-2xl p-7 flex flex-col gap-5 hp-reveal ${inView ? 'hp-in' : ''}`}
              style={{ transitionDelay: `${i * 140}ms` }}
            >
              <div className="flex gap-1">
                {Array.from({ length: t.stars }).map((_, si) => (
                  <Star key={si} size={14} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed flex-1">"{t.quote}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className="w-9 h-9 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 font-black text-sm flex-shrink-0">
                  {t.name[0]}
                </div>
                <div>
                  <div className="text-gray-900 text-sm font-semibold">{t.name}</div>
                  <div className="text-gray-400 text-xs">{t.handle} · {t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 8: Platform Ticker ───────────────────────────────────────────────

const HANDLES = [
  '@ArielHernandez','@Gaetano','@TheValueTrader','@Serenity','@NickDrendel',
  '@CKCapital','@TheLongInvestor','@SixSigmaCapital','@JerryCapital','@MMWealthOfficial',
  '@TradingWithAlpha','@BullBearReport','@FinTwit100','@MarketWhisperer','@AlphaSeeker',
];

const PLATFORMS = [
  { label: 'Twitter / X', cls: 'text-gray-800', svg: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
  { label: 'YouTube',     cls: 'text-red-500',  svg: <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
  { label: 'TikTok',      cls: 'text-gray-900', svg: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/></svg> },
  { label: 'Instagram',   cls: 'text-pink-500', svg: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg> },
];

function PlatformTicker() {
  const ref    = useRef(null);
  const inView = useInView(ref, 0.1);

  return (
    <section className="py-24 bg-gray-50 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className={`text-center mb-12 hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel>Multi-Platform</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-10">Monitoring Finfluencers Across</h2>
          <div className="flex items-center justify-center gap-6 md:gap-10 flex-wrap">
            {PLATFORMS.map((p, i) => (
              <div
                key={i}
                className={`flex flex-col items-center gap-2 hp-reveal ${inView ? 'hp-in' : ''}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-14 h-14 bg-white border border-gray-200 shadow-sm rounded-2xl flex items-center justify-center hover:scale-110 transition-transform duration-200">
                  <span className={p.cls}>{p.svg}</span>
                </div>
                <span className="text-gray-500 text-xs font-medium">{p.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-b border-gray-200 py-4 overflow-hidden bg-white">
          <div className="hp-ticker-track">
            {[...HANDLES, ...HANDLES].map((h, i) => (
              <span key={i} className="text-gray-300 font-mono text-sm px-6 border-r border-gray-100 whitespace-nowrap">{h}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 9: Pricing ───────────────────────────────────────────────────────

const FALLBACK_PLANS = [
  {
    id: 1, name: 'Browse', price: '0', custom: false,
    description: 'Full Verified Leaderboards\nPredictions Feed — All Calls, All Analysts\n3 Detail Views / Day',
  },
  {
    id: 2, name: 'Pro', price: '19', custom: false,
    description: 'Unlimited Detail Views\nFull Prediction History & Sector Accuracy\nOn-demand AI Validation',
  },
  {
    id: 3, name: 'Custom', price: '0', custom: true,
    description: 'Exclusive Data Modeling & Private API Access\nSmart Alerts & Watchlists\nEarly Access to Beta Features',
  },
];

const TIER_CONFIG = [
  { badge: null,           btnCls: 'bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-200',                  btnLabel: 'Start Free' },
  { badge: 'Most Popular', btnCls: 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/25 hp-btn-shimmer', btnLabel: 'Go Pro'     },
  { badge: null,           btnCls: '',                                                                                     btnLabel: ''           },
];

const CUSTOM_PLAN = FALLBACK_PLANS[2];

function PricingSection() {
  const [plans,   setPlans]   = useState(FALLBACK_PLANS.slice(0, 2));
  const [loading, setLoading] = useState(true);
  const ref    = useRef(null);
  const inView = useInView(ref, 0.1);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/payments/subscription/plans/?tenant=analyst_consumer`)
      .then(res => {
        const monthly = res.data
          .filter(p => p.billing_interval === 'monthly' && p.is_active)
          .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
          .slice(0, 2);
        if (monthly.length) setPlans(monthly);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const displayPlans = [...plans, CUSTOM_PLAN];

  return (
    <section id="pricing" className="py-24 bg-gray-50 px-6">
      <div className="max-w-5xl mx-auto">
        <div ref={ref} className={`text-center mb-14 hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel>Pricing</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Simple, Transparent Pricing</h2>
          <p className="text-gray-500 text-lg">Start free. Upgrade when you need more.</p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[0,1,2].map(i => <div key={i} className="bg-gray-200 rounded-2xl h-80 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {displayPlans.map((plan, idx) => {
              const t     = TIER_CONFIG[idx] ?? TIER_CONFIG[0];
              const price = parseFloat(plan.price);
              const isHl  = idx === 1;
              const feats = plan.description
                ? plan.description.split(/[\n,]/).filter(Boolean).map(f => f.trim())
                : [];
              const isCustom = plan.custom === true;
              return (
                <div
                  key={plan.custom ? 'custom' : plan.id}
                  className={`bg-white rounded-2xl p-8 flex flex-col relative border transition-all duration-500 hp-reveal ${inView ? 'hp-in' : ''} ${
                    isHl
                      ? 'ring-2 ring-blue-500 shadow-2xl shadow-blue-500/10 scale-[1.03] border-blue-200'
                      : 'border-gray-200 shadow-sm hover:shadow-md'
                  }`}
                  style={{ transitionDelay: `${idx * 140}ms` }}
                >
                  {t.badge && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap shadow-lg shadow-blue-500/30">
                      {t.badge}
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-gray-900 text-xl font-black mb-4">{plan.name}</h3>
                    {isCustom ? (
                      <div>
                        <span className="text-5xl font-black text-gray-900">POA</span>
                        <p className="text-gray-400 text-sm mt-1">Price On Application</p>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-5xl font-black text-gray-900">${price === 0 ? '0' : price}</span>
                        {price > 0 && <span className="text-gray-400 text-sm">/month</span>}
                      </div>
                    )}
                  </div>
                  <ul className="flex-1 space-y-3 mb-8">
                    {feats.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3 text-sm text-gray-600">
                        <Check size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isCustom ? (
                    <a
                      href={`mailto:${SALES_EMAIL}`}
                      className="w-full py-3.5 rounded-xl font-bold text-sm transition-all bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 text-center block"
                    >
                      Contact Sales
                    </a>
                  ) : (
                    <button
                      onClick={() => { window.location.href = '/login'; }}
                      className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${t.btnCls}`}
                    >
                      {t.btnLabel}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Section 10: CLI Showcase ────────────────────────────────────────────────

const CLI_SLIDES = [
  {
    cmd: 'traderbro analyst list --sort return --limit 3 --json',
    output: `{
  "results": [
    { "name": "PlayBookTrading", "avg_return": 172.6, "predictions": 62 },
    { "name": "StockResearchWS", "avg_return": 145.1, "predictions": 14 },
    { "name": "crux_capital",    "avg_return": 138.4, "predictions": 31 }
  ]
}`,
  },
  {
    cmd: "traderbro symbol trending --since 7d --json \\\n  | jq '.results[:4] | .[].ticker'",
    output: `"NASDAQ:NVDA"
"NASDAQ:TSLA"
"NYSE:PLTR"
"NASDAQ:AAPL"`,
  },
  {
    cmd: "traderbro analyst sector-map --period 1m --json \\\n  | jq '.rows | sort_by(-.avg_return) | .[0:3]'",
    output: `[
  { "sector": "Technology",  "avg_return": 18.4 },
  { "sector": "Energy",      "avg_return": 12.1 },
  { "sector": "Financials",  "avg_return":  9.7 }
]`,
  },
];

function CLIShowcase() {
  const ref    = useRef(null);
  const inView = useInView(ref, 0.15);
  const [slide, setSlide]     = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setSlide((s) => (s + 1) % CLI_SLIDES.length);
        setVisible(true);
      }, 350);
    }, 3800);
    return () => clearInterval(id);
  }, []);

  const { cmd, output } = CLI_SLIDES[slide];

  return (
    <section className="py-24 bg-white px-6">
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className={`hp-reveal ${inView ? 'hp-in' : ''}`}>

          {/* Header */}
          <div className="text-center mb-12">
            <SectionLabel>For AI Agents &amp; Developers</SectionLabel>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-5">
              TraderBro in Your Terminal.<br />In Your Agent.
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
              Query analyst rankings, predictions, and sector data with a single command.
              Pipe JSON straight into Claude, build automated research workflows, or just explore from the terminal.
            </p>
          </div>

          {/* Two-column body */}
          <div className="flex flex-col lg:flex-row gap-10 items-start">

            {/* Terminal window */}
            <div className="w-full lg:w-[55%] rounded-2xl overflow-hidden border border-gray-200 shadow-lg flex-shrink-0">
              {/* Chrome bar */}
              <div className="flex items-center gap-1.5 px-4 py-3 bg-[#1c1c1e] border-b border-white/10">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-xs text-white/30 font-mono">zsh</span>
                <span className="ml-auto text-[10px] text-white/25 font-mono border border-white/15 px-2 py-0.5 rounded">
                  example output
                </span>
              </div>
              {/* Terminal body */}
              <div className="bg-[#0d1117] px-5 py-5 font-mono text-sm min-h-[220px]">
                {/* Prompt + command */}
                <div
                  className="transition-opacity duration-300"
                  style={{ opacity: visible ? 1 : 0 }}
                >
                  <p className="text-white/90 whitespace-pre-wrap">
                    <span className="text-emerald-400 select-none">$ </span>{cmd}
                  </p>
                  <pre className="mt-3 text-[#8b949e] text-xs leading-relaxed whitespace-pre-wrap overflow-x-auto">
                    {output}
                  </pre>
                  <span className="inline-block w-2 h-4 bg-emerald-400/70 mt-2 animate-pulse" />
                </div>
              </div>
              {/* Slide dots */}
              <div className="flex items-center justify-center gap-1.5 py-3 bg-[#0d1117] border-t border-white/5">
                {CLI_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setVisible(false); setTimeout(() => { setSlide(i); setVisible(true); }, 350); }}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${i === slide ? 'bg-emerald-400' : 'bg-white/20'}`}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Value props */}
            <div className="flex-1 flex flex-col gap-7 justify-center pt-2">
              {[
                {
                  icon: '{}',
                  title: '--json everywhere',
                  desc: 'Every command outputs clean, structured JSON. Pipe directly into jq, Claude tool calls, or your own scripts.',
                },
                {
                  icon: '⚡',
                  title: 'Skills built in',
                  desc: 'traderbro skills list exposes step-by-step workflow chains Claude can follow for common research tasks — no prompt engineering needed.',
                },
                {
                  icon: '⌘',
                  title: 'One-line install',
                  desc: 'brew install traderbro/tap/traderbro — then configure with your API key and you\'re querying in under a minute.',
                },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center text-sm font-mono font-bold text-gray-600 flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 mb-0.5">{title}</p>
                    <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}

              {/* Compatible AI agents — prominent block */}
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                  Plug into the AI agent you already use
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Claude */}
                  <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 hover:border-[#CC785C]/40 hover:shadow-sm transition-all">
                    <span className="w-9 h-9 rounded-xl bg-[#CC785C] flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white"><path d="M13.827 3.41c-.892-1.817-3.447-1.817-4.34 0L2.65 16.56C1.818 18.26 3.054 20.25 4.97 20.25h14.747c1.916 0 3.152-1.99 2.322-3.69L13.827 3.41zm-2.17 1.006a.75.75 0 011.342 0l7.188 14.627a.75.75 0 01-.671 1.082H4.97a.75.75 0 01-.67-1.082l7.357-14.627z"/></svg>
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-800 leading-none mb-0.5">Claude</p>
                      <p className="text-[11px] text-gray-400">Anthropic</p>
                    </div>
                  </div>
                  {/* ChatGPT */}
                  <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 hover:border-[#10A37F]/40 hover:shadow-sm transition-all">
                    <span className="w-9 h-9 rounded-xl bg-[#10A37F] flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white"><path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.032.067L9.73 19.95a4.5 4.5 0 01-6.13-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/></svg>
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-800 leading-none mb-0.5">ChatGPT</p>
                      <p className="text-[11px] text-gray-400">OpenAI</p>
                    </div>
                  </div>
                  {/* Hermes */}
                  <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 hover:border-[#7C3AED]/40 hover:shadow-sm transition-all">
                    <span className="w-9 h-9 rounded-xl bg-[#7C3AED] flex items-center justify-center flex-shrink-0 shadow-sm text-white text-sm font-black">{AGENT_NAME.charAt(0)}</span>
                    <div>
                      <p className="text-sm font-bold text-gray-800 leading-none mb-0.5">{AGENT_NAME}</p>
                      <p className="text-[11px] text-gray-400">TraderBro</p>
                    </div>
                  </div>
                  {/* Openclaw */}
                  <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 hover:border-[#2563EB]/40 hover:shadow-sm transition-all">
                    <span className="w-9 h-9 rounded-xl bg-[#2563EB] flex items-center justify-center flex-shrink-0 shadow-sm text-white text-sm font-black">O</span>
                    <div>
                      <p className="text-sm font-bold text-gray-800 leading-none mb-0.5">Openclaw</p>
                      <p className="text-[11px] text-gray-400">Self-hosted</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 mt-2">
                <a
                  href="https://t.me/traderbro_cli_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#229ED9] hover:bg-[#1a8bbf] text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                  </svg>
                  Try Demo on Telegram
                </a>
                <a
                  href="https://docs.traderbro.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold rounded-xl transition-colors"
                >
                  Read the Docs <ArrowRight size={15} />
                </a>
                <Link
                  to="/settings"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 hover:border-gray-400 text-gray-700 text-sm font-semibold rounded-xl transition-colors"
                >
                  Get API Key (Settings) <ArrowRight size={15} />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 11: Email Capture ────────────────────────────────────────────────

function EmailCapture() {
  const [email,     setEmail]     = useState('');
  const [submitted, setSubmitted] = useState(false);
  const ref    = useRef(null);
  const inView = useInView(ref, 0.2);

  return (
    <section className="py-24 bg-gray-50 px-6">
      <div className="max-w-xl mx-auto text-center">
        <div ref={ref} className={`hp-reveal ${inView ? 'hp-in' : ''}`}>
          <SectionLabel>Newsletter</SectionLabel>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Get the Weekly Accuracy Report</h2>
          <p className="text-gray-500 mb-10 leading-relaxed">
            Every Friday: who was right, who was wrong, and what the data says this week.
          </p>
          {submitted ? (
            <div className="text-emerald-600 font-semibold text-lg">✓ You're in. See you Friday.</div>
          ) : (
            <form
              onSubmit={e => { e.preventDefault(); if (email) setSubmitted(true); }}
              className="flex gap-3"
            >
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                required
              />
              <button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3 rounded-xl transition-colors hp-btn-glow hp-btn-shimmer whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Section 12: Footer ───────────────────────────────────────────────────────

function HomepageFooter() {
  return (
    // Dark footer — intentional contrast bookend on light-theme page
    <footer className="bg-gray-900 border-t border-gray-800 py-14 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <div className="mb-3">
              <img src={TraderBroLogo} alt="TraderBro" className="h-7 w-auto select-none" style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              The credit score for financial influencers.<br />Zero opinions — just math.
            </p>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-5">Product</div>
            <div className="space-y-3">
              {[['Leaderboard','/leaderboard'],['Predictions','/predictions'],['Symbols','/symbols'],['Pricing','#pricing']].map(([label, href]) => (
                <div key={label}>
                  {href.startsWith('#')
                    ? <a href={href} className="text-gray-500 hover:text-white text-sm transition-colors block">{label}</a>
                    : <Link to={href} className="text-gray-500 hover:text-white text-sm transition-colors block">{label}</Link>
                  }
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-5">Legal</div>
            <div className="space-y-3">
              <span className="text-gray-500 text-sm block">Privacy</span>
              <span className="text-gray-500 text-sm block">Terms</span>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-gray-600 text-xs">© 2026 TraderBro. All rights reserved.</span>
          <a href="https://twitter.com/traderbro" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white text-sm transition-colors">
            @TraderBro on 𝕏
          </a>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function TraderBroHomepage() {
  const { predictions, symbols, semiconductorAnalysts } = useHomepageData();

  return (
    <div className="bg-white min-h-screen overflow-x-clip font-sans">
      <ScrollProgressBar />
      <div className="sticky top-0 z-50">
        <PredictionTicker predictions={predictions} />
        <Navbar />
      </div>
      <HeroSection predictions={predictions} symbols={symbols} />
      <LeaderboardPreview />
      <PredictionFeedSim predictions={predictions} />
      <HowItWorksDark />
      <SymbolBentoGrid symbols={symbols} />
      <PerplexityComparison semiconductorAnalysts={semiconductorAnalysts} />
      <TestimonialsStrip />
      <PlatformTicker />
      <PricingSection />
      <CLIShowcase />
      <EmailCapture />
      <HomepageFooter />
      <ToastNotifications predictions={predictions} />
    </div>
  );
}
